import React from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, Clock, DollarSign, MapPin, 
  CheckSquare, Square, MoreVertical 
} from 'lucide-react';
import { ASSET_CATEGORIES, ASSET_STATUSES, LOCATIONS } from '../../data/mockData';

const AssetGrid = ({ assets, selectedAssets, onSelectAsset, onSelectAssets }) => {
  const getStatusColor = (status) => {
    return ASSET_STATUSES[status]?.color || 'gray';
  };

  const getLocationName = (locationId) => {
    return LOCATIONS.find(loc => loc.id === locationId)?.name || locationId;
  };

  const handleSelect = (asset, e) => {
    e.stopPropagation();
    if (selectedAssets.includes(asset.id)) {
      onSelectAssets(selectedAssets.filter(id => id !== asset.id));
    } else {
      onSelectAssets([...selectedAssets, asset.id]);
    }
  };

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {assets.map((asset) => (
          <motion.div
            key={asset.id}
            layoutId={asset.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            whileHover={{ scale: 1.02 }}
            onClick={() => onSelectAsset(asset)}
            className="relative group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all"
          >
            {/* Selection Checkbox */}
            <button
              onClick={(e) => handleSelect(asset, e)}
              className="absolute top-2 left-2 z-10"
            >
              {selectedAssets.includes(asset.id) ? (
                <CheckSquare className="w-5 h-5 text-[#43302b]" />
              ) : (
                <Square className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
              )}
            </button>

            {/* Actions Menu */}
            <button className="absolute top-2 right-2 z-10 p-1 rounded-lg hover:bg-gray-100">
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </button>

            {/* Asset Image or Icon */}
            <div className="h-48 bg-gray-100 flex items-center justify-center">
              {ASSET_CATEGORIES.find(cat => cat.id === asset.category)?.icon && (
                <div className={`text-${getStatusColor(asset.status)}-500`}>
                  {React.createElement(
                    ASSET_CATEGORIES.find(cat => cat.id === asset.category)?.icon,
                    { size: 48 }
                  )}
                </div>
              )}
            </div>

            {/* Asset Info */}
            <div className="p-4">
              <div className="mb-3">
                <h3 className="text-sm font-medium text-gray-900 truncate">
                  {asset.name}
                </h3>
                <p className="text-xs text-gray-500">{asset.id}</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <MapPin className="w-3 h-3" />
                  {getLocationName(asset.location)}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Calendar className="w-3 h-3" />
                  {new Date(asset.purchaseDate).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Clock className="w-3 h-3" />
                  Next maintenance: {new Date(asset.nextMaintenance).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <DollarSign className="w-3 h-3" />
                  ${asset.currentValue.toLocaleString()}
                </div>
              </div>

              {/* Status Badge */}
              <div className="mt-4 flex items-center justify-between">
                <span
                  className={`
                    px-2 py-1 rounded-full text-xs font-medium
                    bg-${getStatusColor(asset.status)}-100
                    text-${getStatusColor(asset.status)}-700
                  `}
                >
                  {ASSET_STATUSES[asset.status]?.label || asset.status}
                </span>
                <span className="text-xs text-gray-500">
                  {asset.department}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AssetGrid; 